import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, extractErr, mediaUrl, storePublicBase } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Search, Save, Upload, Loader2, ExternalLink, Bot, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFileUpload } from "@/lib/uploadProgress";

const DEF = {
  meta_title: "", meta_description: "", meta_keywords: "",
  og_image: "", og_title: "", twitter_handle: "",
  focus_keyword: "", default_language: "en",
  bing_site_verification: "",
  geo_placename: "", geo_region: "",
  robots_index: true, json_ld_enabled: true,
  ai_index: true, ai_description: "",
};

export default function AdminSeo() {
  const { t } = useTranslation();
  const uploadFile = useFileUpload();
  const [seo, setSeo] = useState(null);
  const [slug, setSlug] = useState("");
  const [tenant, setTenant] = useState(null);
  const [storeName, setStoreName] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/merchant/config");
      setSeo({ ...DEF, ...(data?.seo || {}) });
      setStoreName(data?.store_info?.name || "");
      const me = await api.get("/auth/merchant/me").catch(() => null);
      setSlug(me?.data?.tenant?.slug || "");
      setTenant(me?.data?.tenant || null);
    } catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/config/seo", seo);
      setSeo({ ...DEF, ...(data?.seo || seo) });
      toast.success("SEO settings saved");
    } catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };

  const uploadOg = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", "seo");
      fd.append("file", file);
      const data = await uploadFile("/merchant/media/upload", fd);
      setSeo((s) => ({ ...s, og_image: data.url }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(extractErr(e)); } finally { setUploading(false); }
  };

  if (!seo) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}</div>;

  const previewTitle = seo.meta_title || `${storeName || "Your store"} — Online Store`;
  const previewDesc = seo.meta_description || `Shop online at ${storeName || "your store"}. Fast delivery, best prices, real support.`;
  const publicBase = storePublicBase(tenant || { slug });
  const previewUrl = publicBase.replace(/^https?:\/\//, "");

  return (
    <div className="space-y-6 max-w-3xl pb-24">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Search className="w-6 h-6 text-blue-600" /> {t("seo.title")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("seo.subtitle")}</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="admin-seo-save-top" className="bg-slate-900 hover:bg-slate-800 shrink-0">
          {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{t("common.save")}</> : <><Save className="w-4 h-4 mr-1.5" />{t("common.save")}</>}
        </Button>
      </div>

      {/* Google preview */}
      <Card className="p-5">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Google preview</div>
        <div className="rounded-lg border p-4 bg-white">
          <div className="text-xs text-slate-600 flex items-center gap-1">{previewUrl}</div>
          <div className="text-[#1a0dab] text-lg leading-snug truncate">{previewTitle}</div>
          <div className="text-sm text-slate-600 line-clamp-2">{previewDesc}</div>
        </div>
      </Card>

      {/* Search appearance */}
      <Card className="p-5 space-y-4">
        <div className="font-display font-bold text-slate-900">Search &amp; social</div>
        <div className="space-y-1">
          <Label>Meta title</Label>
          <Input data-testid="admin-seo-title" value={seo.meta_title} onChange={(e) => setSeo({ ...seo, meta_title: e.target.value })} placeholder={`${storeName || "Your store"} — Online Store`} />
          <p className="text-xs text-slate-400">Shown as the blue headline on Google. ~55–60 characters.</p>
        </div>
        <div className="space-y-1">
          <Label>Meta description</Label>
          <Textarea data-testid="admin-seo-description" rows={3} value={seo.meta_description} onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })} placeholder="A short, compelling summary of your store." />
          <p className="text-xs text-slate-400">The grey text under the title. ~150–160 characters.</p>
        </div>
        <div className="space-y-1">
          <Label>Keywords</Label>
          <Input data-testid="admin-seo-keywords" value={seo.meta_keywords} onChange={(e) => setSeo({ ...seo, meta_keywords: e.target.value })} placeholder="fresh fruits, grocery delivery, …" />
          <p className="text-xs text-slate-400">Comma-separated. Optional.</p>
        </div>
        <div className="space-y-1">
          <Label>Social share image</Label>
          <div className="flex items-center gap-3">
            <div className="w-28 h-16 rounded-lg border overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">
              {seo.og_image ? <img src={mediaUrl(seo.og_image)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">None</span>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadOg(e.target.files?.[0])} data-testid="admin-seo-og-file" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="admin-seo-og-upload">
              <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">Recommended 1200×630 px for social sharing. Layout is responsive if you use another size.</p>
          <Input className="mt-2 text-xs font-mono-nums" value={seo.og_image} onChange={(e) => setSeo({ ...seo, og_image: e.target.value })} placeholder="Image URL (or use Upload)" data-testid="admin-seo-og-url" />
        </div>
        <div className="space-y-1">
          <Label>Social title override <span className="text-slate-400 text-xs">optional</span></Label>
          <Input data-testid="admin-seo-og-title" value={seo.og_title} onChange={(e) => setSeo({ ...seo, og_title: e.target.value })} placeholder="Leave blank to use meta title" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Focus keyword</Label>
            <Input data-testid="admin-seo-focus" value={seo.focus_keyword} onChange={(e) => setSeo({ ...seo, focus_keyword: e.target.value })} placeholder="e.g. organic groceries Delhi" />
          </div>
          <div className="space-y-1">
            <Label>Store language</Label>
            <Input data-testid="admin-seo-lang" value={seo.default_language} onChange={(e) => setSeo({ ...seo, default_language: e.target.value })} placeholder="en" />
            <p className="text-xs text-slate-400">Sets the HTML lang (en, hi, ta, te, mr…).</p>
          </div>
        </div>
        <div className="space-y-1">
          <Label>X / Twitter handle</Label>
          <Input data-testid="admin-seo-twitter" value={seo.twitter_handle} onChange={(e) => setSeo({ ...seo, twitter_handle: e.target.value })} placeholder="@yourstore" />
        </div>
        <div className="space-y-1">
          <Label>Bing Webmaster verification</Label>
          <Input data-testid="admin-seo-bing" className="font-mono-nums text-xs" value={seo.bing_site_verification} onChange={(e) => setSeo({ ...seo, bing_site_verification: e.target.value })} placeholder="msvalidate.01 content value" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>City / locality</Label>
            <Input data-testid="admin-seo-geo-place" value={seo.geo_placename} onChange={(e) => setSeo({ ...seo, geo_placename: e.target.value })} placeholder="e.g. Bengaluru" />
          </div>
          <div className="space-y-1">
            <Label>Region code</Label>
            <Input data-testid="admin-seo-geo-region" value={seo.geo_region} onChange={(e) => setSeo({ ...seo, geo_region: e.target.value })} placeholder="IN-KA" />
            <p className="text-xs text-slate-400">ISO region, e.g. IN-DL, IN-MH. Helps local search.</p>
          </div>
        </div>
        <label className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
          <span className="text-sm text-slate-800">Allow search engines to index my store
            <span className="block text-xs text-slate-500">Turn off to hide your store from Google (noindex).</span>
          </span>
          <Switch checked={seo.robots_index !== false} onCheckedChange={(v) => setSeo({ ...seo, robots_index: v })} data-testid="admin-seo-robots" />
        </label>
        <label className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
          <span className="text-sm text-slate-800">Organization schema (JSON-LD)
            <span className="block text-xs text-slate-500">Helps Google show your store as a business in results.</span>
          </span>
          <Switch checked={seo.json_ld_enabled !== false} onCheckedChange={(v) => setSeo({ ...seo, json_ld_enabled: v })} data-testid="admin-seo-jsonld" />
        </label>
      </Card>

      <Card className="p-5 space-y-4" data-testid="admin-seo-ai-card">
        <div className="font-display font-bold text-slate-900 flex items-center gap-2"><Bot className="w-4 h-4 text-violet-600" /> AI search engines</div>
        <p className="text-sm text-slate-500">Control how ChatGPT, Perplexity, Claude, Gemini and similar bots see your store. We publish <span className="font-mono text-xs">llms.txt</span> and optional robots rules.</p>
        <label className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
          <span className="text-sm text-slate-800">Allow AI crawlers
            <span className="block text-xs text-slate-500">Off = ask GPTBot, ClaudeBot, PerplexityBot and others not to index.</span>
          </span>
          <Switch checked={seo.ai_index !== false} onCheckedChange={(v) => setSeo({ ...seo, ai_index: v })} data-testid="admin-seo-ai-index" />
        </label>
        <div className="space-y-1">
          <Label>AI summary <span className="text-slate-400 text-xs">shown in llms.txt</span></Label>
          <Textarea data-testid="admin-seo-ai-desc" rows={3} value={seo.ai_description} onChange={(e) => setSeo({ ...seo, ai_description: e.target.value })} placeholder="A plain-language description of what you sell, who it is for, and your city / delivery area." />
        </div>
        {slug ? (
          <div className="flex flex-wrap gap-4">
            <a href={`${API}/seo/${slug}/llms.txt`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="admin-seo-llms">
              Preview llms.txt <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a href={`${API}/seo/${slug}/robots.txt`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="admin-seo-robots-file">
              Preview robots.txt <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        ) : null}
      </Card>

      <Card className="p-5 space-y-3">
        <div className="font-display font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" /> Ads &amp; analytics</div>
        <p className="text-sm text-slate-600">Google Analytics, Tag Manager, Search Console, Clarity and Meta Pixel have moved to <Link to="/admin/ads" className="text-violet-700 underline font-medium">App Store → Ads Tool</Link>.</p>
        {slug ? (
          <a href={publicBase} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="admin-seo-view-store">
            View my storefront <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ) : null}
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="admin-seo-save-bottom" className="bg-slate-900 hover:bg-slate-800 shadow-lg">
          {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-1.5" />Save changes</>}
        </Button>
      </div>
    </div>
  );
}
